import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { ErrorBoundary } from '@parkify/ui/error-boundary'
import App from './App'
import { installPreloadErrorRecovery } from './chunkRecovery'
import { AppErrorFallback, onAppError } from './components/ErrorFallbacks'
import { AuthProvider } from './auth/AuthContext'
import { DEMO, installDemo } from './demo'
import '@parkify/brand-tokens/tokens.css'
import './index.css'

// Sales demo mode (VITE_DEMO=1 builds only). Seeds an operator session + a
// fixture-serving fetch interceptor BEFORE React mounts. Constant-folds away in
// the real production build. See src/demo/index.ts.
if (DEMO) installDemo()

// Stale-chunk recovery (one throttled reload after a deploy) lives in
// ./chunkRecovery; the root boundary's fallback + catch handler that use it are
// in ./components/ErrorFallbacks. See both for the full story.
installPreloadErrorRecovery()

const GOOGLE_CLIENT_ID = import.meta.env.VITE_AUTH_GOOGLE_CLIENT_ID as string | undefined

// Keep App's existing lazy descendant routes and provider order. A data router
// gives draft blockers ownership of PUSH/REPLACE and browser POP navigation.
const router = createBrowserRouter([{
  path: '*',
  element: (
    // Root boundary: nothing ever renders blank, and stale chunks reload.
    // App.tsx mounts a second, screen-scoped boundary around <Routes> so a
    // single broken screen keeps the sidebar; this one catches everything
    // else (auth, the shell itself) and any stale chunk handed up to it.
    <ErrorBoundary fallback={AppErrorFallback} onError={onAppError}>
      {/* GoogleOAuthProvider must ALWAYS wrap AuthProvider — useGoogleLogin
          throws at hook-init without it. When VITE_AUTH_GOOGLE_CLIENT_ID is
          unset we still mount with a harmless placeholder id (the GSI script
          just loads; nothing calls it), so the console boots and the email
          magic-link path stays usable. The sign-in card hides the Google
          button whenever the real id is absent (see AuthContext.googleConfigured),
          so the placeholder is never actually exercised. */}
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || 'unconfigured.apps.googleusercontent.com'}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  ),
}])

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
)
